import { Injectable } from '@angular/core';
import { Storage as FBStorage, ref, uploadBytesResumable, getDownloadURL, uploadString, StringFormat, deleteObject, uploadBytes } from '@angular/fire/storage';
import { Storage } from '@ionic/storage-angular';
import { FirebaseUpload } from '@elitetrainr/capacitor-firebase-upload';
import { Auth } from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/angular-ivy';
import { NativeFirebaseAuthBridgeService } from './native-firebase-auth-bridge.service';

export type FbPutFileProgress = {
  bytesTransferred: number;
  totalBytes: number;
  percent: number;
};

export type FbPutFileProgressCallback = (progress: FbPutFileProgress) => void;

export type FbPutFileOptions = {
  /** Prefer for large videos — uses resumable upload + idle/stall detection. */
  resumable?: boolean;
  onProgress?: FbPutFileProgressCallback;
};

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private _storage: Storage | null = null;

  constructor(
    private firebaseAuth: Auth,
    private fbStorage: FBStorage,
    private storage: Storage,
    private nativeFirebaseAuthBridge: NativeFirebaseAuthBridgeService,
  ) {
    this.init();
  }

  async init() {
    // If using, define drivers here: await this.storage.defineDriver(/*...*/);
    const storage = await this.storage.create();
    this._storage = storage;
  }

  // Create and expose methods that users of this service can
  // call, for example:
  public set(key: string, value: any) {
    this._storage?.set(key, value);
  }

    // Create and expose methods that users of this service can
  // call, for example:
  public get(key: string) {
    return this._storage?.get(key);
  }

  public remove(key: string) {
    this._storage?.remove(key);
  }
  
  async uploadTempfile(filename: string, data: Blob): Promise<string> {
    return this.fbPutFile(`temp/${filename}`, data);
  }

  isBlobLike(x: any): x is Blob {
    return x && typeof x.size === 'number' && typeof x.type === 'string' && typeof x.slice === 'function';
  }

private isNativeVideoFile(path: string): boolean {
  const ext = path.split('.').pop()?.toLowerCase();
  if (!ext) return false;
  return ['mp4', 'mov', 'm4v', 'webm', 'mkv', 'mpeg', 'mpg', '3gp'].includes(ext);
}

private getMimeFromPath(path: string): string | undefined {
  const ext = path.split('.').pop()?.toLowerCase();
  if (!ext) return;
  const map: Record<string,string> = {
    // Video
    mp4: 'video/mp4', mov: 'video/quicktime', m4v: 'video/x-m4v',
    // Image
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic',
    // Audio
    m4a: 'audio/m4a', mp3: 'audio/mpeg', aac: 'audio/aac', wav: 'audio/wav', webm: 'audio/webm', ogg: 'audio/ogg'
  };
  return map[ext];
}

/**
 * Maps native upload plugin errors to user-facing messages so the UI can show the real cause
 * (e.g. network issue, auth, file not found) instead of a generic error.
 */
private getUserFacingUploadErrorMessage(error: any): string {
  const msg = (error?.message || String(error || '')).toLowerCase();
  // Network / connection
  if (
    /network|connection|timeout|econnreset|econnrefused|failed to fetch|unable to resolve host|no internet|offline/.test(msg) ||
    /upload failed with status 5\d\d/.test(msg) ||
    /upload failed with status 408|429/.test(msg)
  ) {
    return 'Upload failed due to a network connection issue. Please check your connection and try again.';
  }
  // Auth
  if (/auth|401|403|unauthorized|token/.test(msg)) {
    return 'Upload failed: authentication error. Please sign in again.';
  }
  // File not found or unreadable
  if (/file does not exist|failed to access file|no such file|enoent/.test(msg)) {
    return 'Upload failed: file not found or could not be read.';
  }
  if (/video compression|compression failed|export session|avassetexport|transformer|media3|transcod/.test(msg)) {
    return 'Video processing failed. Try a shorter clip or a different video.';
  }
  // Keep server error text if we have a clear status (e.g. "Upload failed with status 500: ...")
  if (/upload failed with status \d+/.test(msg)) {
    return (error?.message || String(error)).trim();
  }
  return 'Upload failed. Please try again.';
}

private isNativeMissingUserUploadError(error: unknown): boolean {
  const msg =
    error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string'
      ? (error as { message: string }).message
      : String(error || '');
  return msg.includes('No Firebase Auth user on native');
}

private static readonly FB_UPLOAD_TIMEOUT_MS = 120000; // 120s for small web Firebase upload/get URL
private static readonly FB_UPLOAD_IDLE_TIMEOUT_MS = 120000; // no progress for 120s → stall
/** Generous hard cap for resumable video uploads; stalls are caught by idle timeout. */
private static readonly FB_RESUMABLE_UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;

private withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

/**
 * Resumable Firebase upload with progress callbacks.
 * Dead uploads are detected via idle timeout; overall cap is a fixed generous limit.
 */
private uploadBytesResumableWithProgress(
  storageRef: ReturnType<typeof ref>,
  body: Blob | ArrayBuffer | Uint8Array,
  meta: { contentType?: string } | undefined,
  onProgress?: FbPutFileProgressCallback
): Promise<string> {
  const totalHint =
    typeof Blob !== 'undefined' && body instanceof Blob
      ? body.size
      : body instanceof ArrayBuffer
        ? body.byteLength
        : ArrayBuffer.isView(body)
          ? body.byteLength
          : 0;

  return new Promise<string>((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, body as Blob | Uint8Array | ArrayBuffer, meta);
    let settled = false;
    let lastProgressAt = Date.now();

    const finish = (fn: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      clearInterval(idleTimer);
      clearTimeout(overallTimer);
      fn();
    };

    const idleTimer = setInterval(() => {
      if (Date.now() - lastProgressAt > StorageService.FB_UPLOAD_IDLE_TIMEOUT_MS) {
        task.cancel();
        finish(() =>
          reject(new Error('Upload stalled. Please check your connection and try again.'))
        );
      }
    }, 5000);

    const overallTimer = setTimeout(() => {
      task.cancel();
      finish(() =>
        reject(
          new Error(
            'Upload timed out. Please try a shorter recording or a better connection.'
          )
        )
      );
    }, StorageService.FB_RESUMABLE_UPLOAD_TIMEOUT_MS);

    task.on(
      'state_changed',
      (snapshot) => {
        lastProgressAt = Date.now();
        const totalBytes = snapshot.totalBytes || totalHint || 0;
        const percent =
          totalBytes > 0
            ? Math.min(100, Math.round((snapshot.bytesTransferred / totalBytes) * 100))
            : 0;
        try {
          onProgress?.({
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes,
            percent,
          });
        } catch {
          // ignore progress callback errors
        }
      },
      (error) => {
        finish(() => reject(error));
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(task.snapshot.ref);
          finish(() => resolve(downloadURL));
        } catch (error) {
          finish(() => reject(error));
        }
      }
    );
  });
}

async fpPutNativeFile(storagePath: string, nativePath: string): Promise<string> {
  const currentUser = this.firebaseAuth.currentUser;

  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  let uploadPath = nativePath;
  let tempCompressedPath: string | null = null;
  const platform = Capacitor.getPlatform();
  const compressOnNative = platform === 'ios' || platform === 'android';
  const isVideo = this.isNativeVideoFile(nativePath);

  try {
    if (compressOnNative) {
      await this.nativeFirebaseAuthBridge.ensureNativeSessionForUpload();
    }

    if (compressOnNative && isVideo) {
      const { outputPath } = await FirebaseUpload.compressVideo({ inputPath: nativePath });
      uploadPath = outputPath;
      tempCompressedPath = outputPath;
    }

    const contentType =
      tempCompressedPath != null
        ? 'video/mp4'
        : this.getMimeFromPath(storagePath) || this.getMimeFromPath(nativePath) || 'application/octet-stream';

    const runUpload = async (forceRefreshIdToken: boolean) => {
      const u = this.firebaseAuth.currentUser;
      if (!u) {
        throw new Error('User not authenticated');
      }
      const authToken = await u.getIdToken(forceRefreshIdToken);
      return FirebaseUpload.uploadFile({
        filePath: uploadPath,
        storagePath,
        authToken,
        metadata: { contentType },
      });
    };

    try {
      const uploadResult = await runUpload(false);
      return uploadResult.downloadURL;
    } catch (error) {
      if (compressOnNative && this.isNativeMissingUserUploadError(error)) {
        await this.nativeFirebaseAuthBridge.ensureNativeSessionForUpload({ force: true });
        const uploadResult = await runUpload(true);
        return uploadResult.downloadURL;
      }
      throw error;
    }
  } catch (error) {
    const rawMessage =
      error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string'
        ? (error as { message: string }).message
        : String(error);
    const rawCode = error && typeof error === 'object' && 'code' in error ? (error as { code: unknown }).code : undefined;
    const userMessage = this.getUserFacingUploadErrorMessage(error);

    const reportable =
      error instanceof Error ? error : new Error(`Native upload plugin: ${rawMessage}`);
    if (!(error instanceof Error) && error != null) {
      (reportable as Error & { cause?: unknown }).cause = error;
    }

    Sentry.captureException(reportable, {
      tags: { upload: 'fpPutNativeFile' },
      extra: {
        storagePath,
        nativePath,
        uploadPath,
        hadTempCompressed: tempCompressedPath != null,
        platform: Capacitor.getPlatform(),
        userFacingMessage: userMessage,
        rawPluginMessage: rawMessage,
        pluginCode: rawCode,
      },
    });

    const err = new Error(userMessage) as Error & { originalError?: unknown; cause?: unknown };
    err.originalError = error;
    err.cause = error;
    throw err;
  } finally {
    if (tempCompressedPath) {
      try {
        await FirebaseUpload.deleteFile({ filePath: tempCompressedPath });
      } catch {
        // Best-effort: temp lives in system tmp if delete fails
      }
    }
  }
}

async fbPutFile(
  path: string,
  dataIn: Blob | File | ArrayBuffer | Uint8Array,
  options?: FbPutFileOptions
): Promise<string> {
  
  const rf = ref(this.fbStorage, path);
  
  let body: Blob | ArrayBuffer | Uint8Array = dataIn as any;
  let contentType: string | undefined;

  // File objects from native file system are references, not actual data
  // Firebase Storage can't read from native file paths, so we need to read the File into memory first
  if (dataIn instanceof File) {
    contentType = dataIn.type || this.getMimeFromPath(path) || 'application/octet-stream';
    body = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as ArrayBuffer);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(dataIn);
    });
  } else if (dataIn instanceof Blob) {
    // Regular Blob (not File) - can use directly, but check if it's from native file system
    contentType = dataIn.type || this.getMimeFromPath(path) || 'application/octet-stream';
    // If it's already a Blob (not File), we can use it directly
    body = dataIn;
  } else if (!(body instanceof ArrayBuffer) && !ArrayBuffer.isView(body)) {
    if (typeof (body as any)?.arrayBuffer === 'function') {
      body = await (body as any).arrayBuffer();
    } else {
      throw new Error(`Cannot convert ${typeof body} to Blob/ArrayBuffer`);
    }
  }

  // Metadata for Firebase Storage
  const meta = contentType 
    ? { contentType }
    : (this.getMimeFromPath(path) ? { contentType: this.getMimeFromPath(path)! } : undefined);

  const useResumable = !!(options?.resumable || options?.onProgress);
  if (useResumable) {
    return this.uploadBytesResumableWithProgress(rf, body, meta, options?.onProgress);
  }

  // Upload to Firebase Storage (small / default path)
  await this.withTimeout(
    uploadBytes(rf, body as any, meta),
    StorageService.FB_UPLOAD_TIMEOUT_MS,
    'Upload timed out. Please try again.'
  );

  const downloadURL = await this.withTimeout(
    getDownloadURL(rf),
    StorageService.FB_UPLOAD_TIMEOUT_MS,
    'Upload completed but download URL timed out. Please try again.'
  );
  
  return downloadURL;
}

  async fbPutString(path: string, data: string, format = StringFormat.DATA_URL): Promise<string> {
    const rf = ref(this.fbStorage, path)
    await uploadString(rf, data, format)
    return getDownloadURL(rf)
  }

  async fbGetBase64(path: string) {
    const rf = ref(this.fbStorage, path)
  }

  fbGetDownloadURL(path: string) {
    const rf = ref(this.fbStorage, path)
    return getDownloadURL(rf)
  }

  fbRemoveFile(path: string) {
    const rf = ref(this.fbStorage, path)
    return deleteObject(rf)
  }

  /**
   * Downloads a file from a Firebase URL and saves it to the device photo library
   * @param firebaseUrl - The Firebase Storage download URL
   * @param fileName - Optional filename (will be generated if not provided)
   * @param fileType - Optional file type ('image' or 'video'), will be detected from URL if not provided
   * @returns Promise that resolves when the file is saved
   */
  async saveFirebaseUrlToDevice(firebaseUrl: string, fileName?: string, fileType?: 'image' | 'video'): Promise<void> {
    console.log('[StorageService] saveFirebaseUrlToDevice called', { firebaseUrl, fileName, fileType });
    
    try {
      // Detect file type from URL if not provided
      if (!fileType) {
        const urlLower = firebaseUrl.toLowerCase();
        console.log('[StorageService] Detecting file type from URL:', urlLower);
        if (urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.m4v') || urlLower.includes('video')) {
          fileType = 'video';
        } else {
          fileType = 'image';
        }
        console.log('[StorageService] Detected file type:', fileType);
      }

      // Generate filename if not provided
      if (!fileName) {
        const extension = fileType === 'video' ? 'mp4' : 'jpg';
        fileName = `saved-${Date.now()}.${extension}`;
        console.log('[StorageService] Generated filename:', fileName);
      }

      const isNative = Capacitor.isNativePlatform();
      console.log('[StorageService] Platform check - isNative:', isNative);
      
      if (isNative) {
        // Use native plugin to download and save directly to photo library
        console.log('[StorageService] Calling FirebaseUpload.saveToPhotoLibrary');
        await FirebaseUpload.saveToPhotoLibrary({
          url: firebaseUrl,
          fileType: fileType
        });
        console.log('[StorageService] File saved to photo library');
      } else {
        // Web: fetch as blob then download. Cross-origin URLs ignore <a download>, which
        // otherwise navigates the current tab to the file URL.
        console.log('[StorageService] Web platform - triggering download');
        const response = await fetch(firebaseUrl, { mode: 'cors' });
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 250);
        console.log('[StorageService] Download triggered');
      }
      
      console.log('[StorageService] saveFirebaseUrlToDevice completed successfully');
    } catch (error) {
      console.error('[StorageService] Error saving Firebase URL to device:', error);
      console.error('[StorageService] Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      throw error;
    }
  }
}